'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useEffect } from 'react';
import { styled } from '@classify/react-core-components/styles';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Box from '@classify/react-core-components/Box';
import Snackbar, { Props as SnackbarProps } from '@classify/react-core-components/Snackbar';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { translate } from '@openclassrooms/translations';
import { useChapterContext } from '../_contexts/ChapterContext';
import ChapterContentWrapper from './ChapterContentWrapper';
import CourseChapterHeader from './CourseChapterHeader';
import CourseFooterWrapper from './CourseFooterWrapper';
import TocToggler from './TocToggler';
import ChapterCompletionBanner from './ChapterCompletionBanner';
import CourseEditionPanel from './CourseEditionPanel';
import CourseTitle from './CourseTitle';
import EditionPanelVariants from '../_constants/EditionPanelVariants';
import CourseNeedLogin from './CourseNeedLogin';
import useTocState from '../../_hooks/useTocState';
const CourseContentWrapper = styled(ContentWrapper)(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  position: 'relative',
  width: 'fill-available',
  paddingTop: spacing(4),
  '& p a, & p code': {
    wordBreak: 'break-word'
  },
  [breakpoints.up('sm')]: {
    paddingLeft: spacing(4),
    paddingRight: spacing(4)
  }
}));
interface Props {
  isTocInitiallyOpen: boolean;
}
export default function Page(t0) {
  const $ = _c(43);
  const {
    isTocInitiallyOpen
  } = t0;
  const {
    canEditCourse,
    currentChapter,
    course
  } = useChapterContext();
  const {
    isConnected
  } = useCurrentUserContext();
  const headerRef = useRef(null);
  const [isTocOpen, setIsTocOpen] = useTocState(isTocInitiallyOpen);
  const [hasCancelledCompletion, setHasCancelledCompletion] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      isOpen: false,
      message: "",
      type: "success"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [snackbar, setSnackbar] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const displayAnchorSnackbar = async () => {
        if (window.location.hash) {
          await navigator.clipboard.writeText(window.location.href);
          setSnackbar({
            message: translate("course.chapter.anchor.copied"),
            type: "success",
            isOpen: true
          });
        }
      };
      window.addEventListener("hashchange", displayAnchorSnackbar);
      return () => {
        window.removeEventListener("hashchange", displayAnchorSnackbar);
      };
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const courseId = course.id;
  const chapterId = currentChapter?.id;
  if (!chapterId) {
    return null;
  }
  const shouldShowCompletionBanner = currentChapter?.isCompleted || hasCancelledCompletion;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CourseChapterHeader headerRef={headerRef} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== shouldShowCompletionBanner) {
    t5 = shouldShowCompletionBanner && <ChapterCompletionBanner onClick={() => setHasCancelledCompletion(true)} />;
    $[4] = shouldShowCompletionBanner;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <div ref={headerRef}>{t4}{t5}</div>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      display: "flex",
      position: "relative",
      pb: 2
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setHasCancelledCompletion(false);
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isTocOpen || $[11] !== setIsTocOpen) {
    t9 = <TocToggler headerRef={headerRef} onItemClick={t8} isOpen={isTocOpen} setIsOpen={setIsTocOpen} />;
    $[10] = isTocOpen;
    $[11] = setIsTocOpen;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== canEditCourse || $[14] !== chapterId || $[15] !== course.publishedAt || $[16] !== courseId) {
    t10 = canEditCourse && <CourseEditionPanel courseId={courseId} chapterId={chapterId} isPublished={Boolean(course?.publishedAt)} variant={EditionPanelVariants.CHAPTER} />;
    $[13] = canEditCourse;
    $[14] = chapterId;
    $[15] = course.publishedAt;
    $[16] = courseId;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = currentChapter?.title;
  let t12;
  if ($[18] !== t11) {
    t12 = <CourseTitle>{t11}</CourseTitle>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isConnected) {
    t13 = !isConnected && <CourseNeedLogin />;
    $[20] = isConnected;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== chapterId || $[23] !== courseId) {
    t14 = <ChapterContentWrapper courseId={courseId} chapterId={chapterId} />;
    $[22] = chapterId;
    $[23] = courseId;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t10 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = <CourseContentWrapper variant="narrow">{t10}{t12}{t13}{t14}</CourseContentWrapper>;
    $[25] = t10;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t9) {
    t16 = <Box sx={t7}>{t9}{t15}</Box>;
    $[30] = t15;
    $[31] = t9;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <CourseFooterWrapper />;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setSnackbar(_temp);
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== snackbar.isOpen || $[36] !== snackbar.message || $[37] !== snackbar.type) {
    t19 = <Snackbar isOpen={snackbar.isOpen} message={snackbar.message} type={snackbar.type} autoHideDuration={2500} onClose={t18} />;
    $[35] = snackbar.isOpen;
    $[36] = snackbar.message;
    $[37] = snackbar.type;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t16 || $[40] !== t19 || $[41] !== t6) {
    t20 = <>{t6}{t16}{t17}{t19}</>;
    $[39] = t16;
    $[40] = t19;
    $[41] = t6;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  return t20;
}
function _temp(prev) {
  return {
    ...prev,
    isOpen: false
  };
}